import React, { ReactNode, useRef, useState, useCallback } from 'react'

import { ActionItemsContainer, ActionText, Button } from './styles'

interface IAction {
  text: string
  color: string
  textColor?: string
  onPress: () => void
}

interface IProps {
  rightActions: IAction[]
  children: ReactNode
}

const SWIPE_THRESHOLD = 40
const ACTION_BUTTON_WIDTH = 80

export function SwipeableRow({ rightActions, children }: IProps) {
  const [translateX, setTranslateX] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const startXRef = useRef(0)
  const currentXRef = useRef(0)
  const isDraggingRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const maxTranslate = rightActions.length * ACTION_BUTTON_WIDTH

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDraggingRef.current = true
      startXRef.current = e.clientX
      currentXRef.current = isOpen ? -maxTranslate : 0
      ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    },
    [isOpen, maxTranslate],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current) return
      const diff = e.clientX - startXRef.current
      const newX = Math.min(0, Math.max(-maxTranslate, currentXRef.current + diff))
      setTranslateX(newX)
    },
    [maxTranslate],
  )

  const handlePointerUp = useCallback(() => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    if (translateX < -SWIPE_THRESHOLD) {
      setTranslateX(-maxTranslate)
      setIsOpen(true)
    } else {
      setTranslateX(0)
      setIsOpen(false)
    }
  }, [translateX, maxTranslate])

  const handleActionPress = useCallback(
    (onPress: () => void) => {
      onPress()
      setTranslateX(0)
      setIsOpen(false)
    },
    [],
  )

  return (
    <div
      style={{ position: 'relative', overflow: 'hidden' }}
      ref={containerRef}
    >
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {rightActions.map((action, index) => (
          <Button
            key={index}
            $backgroundColor={action.color}
            style={{ width: ACTION_BUTTON_WIDTH }}
            onClick={() => handleActionPress(action.onPress)}
          >
            <ActionText color={action.textColor}>{action.text}</ActionText>
          </Button>
        ))}
      </div>

      <div
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDraggingRef.current ? 'none' : 'transform 0.25s ease-out',
          touchAction: 'pan-y',
          backgroundColor: 'inherit',
          position: 'relative',
          zIndex: 1,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {children}
      </div>
    </div>
  )
}
